export interface HomeopathyFact {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'history' | 'principles' | 'remedies' | 'research' | 'practice' | 'curiosity';
  difficulty: 'basic' | 'intermediate' | 'advanced';
  tags: string[];
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface FactReadStatus {
  factId: string;
  readAt: Date;
  familyId?: string;
}

class HomeopathyFactsManager {
  private static instance: HomeopathyFactsManager;
  private facts: HomeopathyFact[] = [];
  private readonly STORAGE_KEY = 'magistral_homeopathy_facts';

  private constructor() {
    this.loadFacts();
    if (this.facts.length === 0) {
      this.initializeDefaultFacts();
    }
  }

  public static getInstance(): HomeopathyFactsManager {
    if (!HomeopathyFactsManager.instance) {
      HomeopathyFactsManager.instance = new HomeopathyFactsManager();
    }
    return HomeopathyFactsManager.instance;
  }

  private loadFacts(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.facts = parsed.map((fact: any) => ({
          ...fact,
          createdAt: new Date(fact.createdAt),
          updatedAt: new Date(fact.updatedAt)
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar curiosidades:', error);
      this.facts = [];
    }
  }

  private saveFacts(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.facts));
    } catch (error) {
      console.error('Erro ao salvar curiosidades:', error);
    }
  }

  private initializeDefaultFacts(): void {
    const defaultFacts: Omit<HomeopathyFact, 'id' | 'createdAt' | 'updatedAt'>[] = [
      {
        title: "O Nascimento da Homeopatia",
        content: "A homeopatia foi criada em 1796 pelo médico alemão Samuel Hahnemann, após ele descobrir que a quinina, usada para tratar malária, causava sintomas similares à doença quando tomada por pessoas saudáveis.",
        category: "history",
        difficulty: "basic",
        tags: ["Samuel Hahnemann", "história", "quinina", "malária"],
        active: true
      },
      {
        title: "Lei dos Semelhantes",
        content: "O princípio fundamental da homeopatia é 'Similia similibus curentur' (semelhante cura semelhante). Isso significa que uma substância que causa sintomas em uma pessoa saudável pode curar sintomas similares em uma pessoa doente.",
        category: "principles",
        difficulty: "basic",
        tags: ["lei dos semelhantes", "princípios", "Hahnemann"],
        active: true
      },
      {
        title: "Dinamização e Potencialização",
        content: "Na homeopatia, quanto mais diluída e dinamizada (agitada) uma substância, mais potente ela se torna. Este processo é chamado de potencialização e vai contra a lógica da medicina convencional.",
        category: "principles",
        difficulty: "intermediate",
        tags: ["dinamização", "potencialização", "diluição"],
        active: true
      },
      {
        title: "Arnica Montana",
        content: "A Arnica é um dos remédios homeopáticos mais conhecidos, usado principalmente para traumas físicos, contusões e dores musculares. É extraída de uma planta das montanhas europeias.",
        category: "remedies",
        difficulty: "basic",
        tags: ["Arnica", "trauma", "contusões", "plantas"],
        active: true
      },
      {
        title: "Belladonna e Seus Usos",
        content: "Belladonna, feita da planta 'deadly nightshade', é usada em homeopatia para febres altas súbitas, inflamações e dores de cabeça pulsáteis. Curiosamente, a planta é altamente tóxica em sua forma natural.",
        category: "remedies",
        difficulty: "intermediate",
        tags: ["Belladonna", "febre", "inflamação", "toxicidade"],
        active: true
      },
      {
        title: "Nux Vomica para o Estresse Moderno",
        content: "Nux Vomica é frequentemente prescrita para pessoas estressadas, workaholics e com problemas digestivos causados pelo estilo de vida moderno. É feita a partir das sementes da árvore Strychnos nux-vomica.",
        category: "remedies",
        difficulty: "intermediate",
        tags: ["Nux Vomica", "estresse", "digestão", "estilo de vida"],
        active: true
      },
      {
        title: "Homeopatia na Veterinária",
        content: "A homeopatia não é usada apenas em humanos! Muitos veterinários utilizam remédios homeopáticos para tratar animais domésticos e de fazenda, com resultados promissores em várias condições.",
        category: "practice",
        difficulty: "basic",
        tags: ["veterinária", "animais", "tratamento"],
        active: true
      },
      {
        title: "O Efeito Placebo Questionado",
        content: "Críticos argumentam que a homeopatia funciona apenas por efeito placebo. No entanto, defensores apontam que bebês e animais também respondem ao tratamento, questionando essa teoria.",
        category: "research",
        difficulty: "advanced",
        tags: ["placebo", "pesquisa", "bebês", "animais"],
        active: true
      },
      {
        title: "Homeopatia no SUS",
        content: "Desde 2006, a homeopatia é oferecida gratuitamente pelo Sistema Único de Saúde (SUS) no Brasil, sendo reconhecida como uma Prática Integrativa e Complementar em Saúde (PICS).",
        category: "practice",
        difficulty: "basic",
        tags: ["SUS", "Brasil", "PICS", "saúde pública"],
        active: true
      },
      {
        title: "Repertório Homeopático",
        content: "O repertório é um índice gigantesco que lista sintomas e os remédios correspondentes. O mais famoso é o 'Repertório de Kent', com mais de 700 páginas de sintomas organizados sistematicamente.",
        category: "practice",
        difficulty: "advanced",
        tags: ["repertório", "Kent", "sintomas", "prescrição"],
        active: true
      },
      {
        title: "Miasmas de Hahnemann",
        content: "Hahnemann desenvolveu a teoria dos miasmas - predisposições hereditárias a certas doenças. Ele identificou três miasmas principais: Psora, Sycosis e Syphilis, base para tratamentos constitucionais.",
        category: "principles",
        difficulty: "advanced",
        tags: ["miasmas", "hereditariedade", "constituição", "Hahnemann"],
        active: true
      },
      {
        title: "Homeopatia e a Água",
        content: "Uma das maiores controvérsias da homeopatia é como a água 'lembra' das substâncias diluídas. Pesquisadores como Jacques Benveniste estudaram a 'memória da água', gerando debates científicos intensos.",
        category: "research",
        difficulty: "advanced",
        tags: ["memória da água", "Benveniste", "controvérsia", "pesquisa"],
        active: true
      },
      {
        title: "Sulphur: O Rei dos Remédios",
        content: "Sulphur é considerado um dos remédios mais importantes da homeopatia, usado para 'destravar' casos difíceis e tratar condições de pele crônicas. É frequentemente o primeiro remédio testado em novos pacientes.",
        category: "remedies",
        difficulty: "intermediate",
        tags: ["Sulphur", "pele", "casos difíceis", "primeiro remédio"],
        active: true
      },
      {
        title: "Homeopatia na Índia",
        content: "A Índia é o país que mais usa homeopatia no mundo, com mais de 200.000 médicos homeopatas registrados. Lá, a homeopatia é integrada ao sistema de saúde nacional e ensinada em universidades.",
        category: "practice",
        difficulty: "basic",
        tags: ["Índia", "médicos", "universidades", "sistema de saúde"],
        active: true
      },
      {
        title: "Potências Centesimais e Decimais",
        content: "Existem diferentes escalas de potencialização: centesimal (C) onde se dilui 1:100, e decimal (X ou D) onde se dilui 1:10. Uma potência 30C significa 30 diluições sucessivas de 1:100.",
        category: "principles",
        difficulty: "intermediate",
        tags: ["potências", "centesimal", "decimal", "diluição"],
        active: true
      }
    ];

    defaultFacts.forEach(factData => {
      this.addFact(factData);
    });
  }

  public getAllFacts(): HomeopathyFact[] {
    return [...this.facts];
  }

  public getActiveFacts(): HomeopathyFact[] {
    return this.facts.filter(fact => fact.active);
  }

  public getFactById(id: string): HomeopathyFact | undefined {
    return this.facts.find(fact => fact.id === id);
  }

  public getFactsByCategory(category: HomeopathyFact['category']): HomeopathyFact[] {
    return this.facts.filter(fact => fact.category === category && fact.active);
  }

  public getFactsByDifficulty(difficulty: HomeopathyFact['difficulty']): HomeopathyFact[] {
    return this.facts.filter(fact => fact.difficulty === difficulty && fact.active);
  }

  public addFact(factData: Omit<HomeopathyFact, 'id' | 'createdAt' | 'updatedAt'>): HomeopathyFact {
    const newFact: HomeopathyFact = {
      ...factData,
      id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.facts.push(newFact);
    this.saveFacts();
    return newFact;
  }

  public updateFact(id: string, updates: Partial<Omit<HomeopathyFact, 'id' | 'createdAt'>>): boolean {
    const factIndex = this.facts.findIndex(fact => fact.id === id);
    if (factIndex === -1) return false;

    this.facts[factIndex] = {
      ...this.facts[factIndex],
      ...updates,
      updatedAt: new Date()
    };

    this.saveFacts();
    return true;
  }

  public deleteFact(id: string): boolean {
    const factIndex = this.facts.findIndex(fact => fact.id === id);
    if (factIndex === -1) return false;

    this.facts.splice(factIndex, 1);
    this.saveFacts();
    return true;
  }

  public toggleFactStatus(id: string): boolean {
    const fact = this.facts.find(f => f.id === id);
    if (!fact) return false;

    fact.active = !fact.active;
    fact.updatedAt = new Date();
    this.saveFacts();
    return true;
  }

  public getTotalCount(): number {
    return this.facts.length;
  }

  public getActiveCount(): number {
    return this.facts.filter(fact => fact.active).length;
  }

  public searchFacts(query: string): HomeopathyFact[] {
    const lowercaseQuery = query.toLowerCase();
    return this.facts.filter(fact => 
      fact.active && (
        fact.title.toLowerCase().includes(lowercaseQuery) ||
        fact.content.toLowerCase().includes(lowercaseQuery) ||
        fact.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
      )
    );
  }
}

export { HomeopathyFactsManager };
export default HomeopathyFactsManager;